import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, merge } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { WebSocketService } from './websocket.service';
import { AuthService } from './auth.service';

export interface MarinaPriceSeason {
  id?: string;
  name: string;
  description?: string;
  seasonStart: string; // ISO date string
  seasonEnd: string; // ISO date string
  recurrence?: 'NONE' | 'ANNUAL';
  createdAt?: string;
  updatedAt?: string;
  priceListId: string;
  priceListName?: string;
  clientId: string;
}

export interface CreateUpdateMarinaPriceSeasonDto {
  name: string;
  description?: string;
  seasonStart: string; // ISO date string
  seasonEnd: string; // ISO date string
  recurrence?: 'NONE' | 'ANNUAL';
  priceListId: string;
  clientId: string;
}

@Injectable({
  providedIn: 'root'
})
export class MarinaPriceSeasonService {
  private apiUrl = '/api/v1/price-seasons';

  constructor(
    private http: HttpClient,
    private router: Router,
    private webSocketService: WebSocketService,
    private authService: AuthService
  ) {
    console.log('[MarinaPriceSeasonService] Service initialized with WebSocket support');
  }

  /**
   * Get all price seasons
   */
  getSeasons(): Observable<MarinaPriceSeason[]> {
    const headers = this.getAuthHeaders();

    return this.http.get<MarinaPriceSeason[]>(this.apiUrl, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching price seasons:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get season by ID
   */
  getSeasonById(id: string): Observable<MarinaPriceSeason> {
    const headers = this.getAuthHeaders();

    return this.http.get<MarinaPriceSeason>(`${this.apiUrl}/${id}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching season by ID:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get seasons by price list ID
   */
  getSeasonsByPriceListId(priceListId: string): Observable<MarinaPriceSeason[]> {
    const headers = this.getAuthHeaders();

    return this.http.get<MarinaPriceSeason[]>(`${this.apiUrl}/price-list/${priceListId}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching seasons by price list ID:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Create a new price season
   */
  createSeason(season: CreateUpdateMarinaPriceSeasonDto): Observable<MarinaPriceSeason> {
    const headers = this.getAuthHeaders();

    return this.http.post<MarinaPriceSeason>(this.apiUrl, season, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error creating price season:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update an existing price season
   */
  updateSeason(id: string, season: Partial<CreateUpdateMarinaPriceSeasonDto>): Observable<MarinaPriceSeason> {
    const headers = this.getAuthHeaders();

    return this.http.put<MarinaPriceSeason>(`${this.apiUrl}/${id}`, season, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error updating price season:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a price season
   */
  deleteSeason(id: string): Observable<void> {
    const headers = this.getAuthHeaders();

    return this.http.delete<void>(`${this.apiUrl}/${id}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error deleting price season:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get authentication headers from localStorage
   */
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('auth_token');
    const credentials = localStorage.getItem('auth_credentials');

    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    } else if (credentials) {
      headers = headers.set('Authorization', `Basic ${credentials}`);
    }

    return headers;
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: HttpErrorResponse): void {
    if (error.status === 401) {
      // Clear stored credentials and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_credentials');
      localStorage.removeItem('current_user');
      this.router.navigate(['/login']);
    }
  }

  /**
   * WebSocket Integration Methods
   */

  /**
   * Get observable for real-time price season updates from WebSocket (create/update)
   */
  getRealtimePriceSeasonUpdates(): Observable<any> {
    const clientIds = this.authService.getClientIds();
    const observables = clientIds.map(clientId => 
      this.webSocketService.subscribe(`/topic/${clientId}.MarinaPriceSeason`)
    );
    return merge(...observables);
  }

  /**
   * Get observable for real-time price season deletions from WebSocket
   */
  getRealtimePriceSeasonDeletions(): Observable<any> {
    const clientIds = this.authService.getClientIds();
    const observables = clientIds.map(clientId => 
      this.webSocketService.subscribe(`/topic/${clientId}.MarinaPriceSeason`)
    );
    return merge(...observables);
  }
}

